"use client"

import * as React from "react"
import {
  Settings,
  Briefcase,
  FolderOpen,
  UserCheck,
  Sliders,
  PanelRight,
  Grid,
  Puzzle,
  ShoppingBag,
  Wrench,
  Clock,
} from "lucide-react"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Account, UserProfile, Language } from "@/types/workspace"

// Sub-Tab Components
import { CompanySettingsTab } from "@/components/settings/tabs/CompanySettingsTab"
import { WorkspacesSettingsTab } from "@/components/settings/tabs/WorkspacesSettingsTab"
import { UsersSettingsTab } from "@/components/settings/tabs/UsersSettingsTab"
import { DeskSettingsTab } from "@/components/settings/tabs/DeskSettingsTab"
import { DockSettingsTab } from "@/components/settings/tabs/DockSettingsTab"
import { BarSettingsTab } from "@/components/settings/tabs/BarSettingsTab"
import { AppsSettingsTab } from "@/components/settings/tabs/AppsSettingsTab"
import { WidgetsSettingsTab } from "@/components/settings/tabs/WidgetsSettingsTab"
import { DeveloperSettingsTab } from "@/components/settings/tabs/DeveloperSettingsTab"

export interface SettingsViewProps {
  isMobile: boolean
  settingsTab: string
  setSettingsTab: (tab: string) => void
  t: Record<string, string>
  profileRole: string
  workspaceRole?: string
  currentAccount: Account | null
  handleSaveCompany?: (data?: any) => Promise<any> | void
  companyName: string
  setCompanyName: (val: string) => void
  companyDocument: string
  setCompanyDocument: (val: string) => void
  companyLogoUrl: string
  setCompanyLogoUrl: (val: string) => void
  companyBannerUrl?: string
  setCompanyBannerUrl?: (val: string) => void
  companyPrimaryColor: string
  setCompanyPrimaryColor: (val: string) => void
  companyCustomDomain: string
  setCompanyCustomDomain: (val: string) => void
  companySamlEnabled: boolean
  setCompanySamlEnabled: (val: boolean) => void
  companySamlIdpEntityId: string
  setCompanySamlIdpEntityId: (val: string) => void
  companySamlSsoUrl: string
  setCompanySamlSsoUrl: (val: string) => void
  companySamlCertificate: string
  setCompanySamlCertificate: (val: string) => void
  accounts: Account[]
  activeAccountId: string
  handleSwitchWorkspace: (id: string) => void
  handleCreateWorkspace: (name: string, accountId: string) => Promise<any>
  handleUpdateWorkspace?: (id: string, name: string) => Promise<any>
  handleDeleteWorkspace?: (id: string) => Promise<any>
  usersList: UserProfile[]
  handleInviteUser: (email: string, name: string, role: string, workspaces?: Array<{ workspaceId: string; role: string }>) => Promise<any> | void
  handleUpdateAccountMember?: (userId: string, data: { name?: string; role?: string; status?: string; customRoleId?: string | null }) => Promise<any> | void
  handleUpdateUserRole?: (userId: string, role: string) => void
  handleRemoveUser: (userId: string) => Promise<any> | void
  wallpaperTab: string
  setWallpaperTab: (tab: string) => void
  mobileWallpaper: string
  setMobileWallpaper: (val: string) => void
  desktopWallpaper: string
  setDesktopWallpaper: (val: string) => void
  customWallpapers?: string[]
  setCustomWallpapers?: any
  useSeparateMobileWallpaper: boolean
  setUseSeparateMobileWallpaper: (val: boolean) => void
  handleSaveWallpaperToDb: (d: string, m: string, sep: boolean) => void
  dockPosition: "bottom" | "left" | "right"
  setDockPosition: (pos: "bottom" | "left" | "right") => void
  dockSize: number
  setDockSize: (size: number) => void
  dockAutoHide: boolean
  setDockAutoHide: (hide: boolean) => void
  topBarConfig: any
  setTopBarConfig: any
  installedApps: any[]
  handleToggleAppStatus: (id: string) => void
  handleUninstallApp: (id: string) => void
  handleInstallApp?: (app: any) => void
  handleRenameApp?: (appId: string, newName: string) => void
  availableWidgets: any[]
  setWidgetsConfig?: React.Dispatch<React.SetStateAction<any[]>>
  pinnedWidgets: string[]
  handleToggleWidgetPin: (id: string) => void
  marketplaceApps: any[]
  globallyInstalledApps: string[]
  developerApps: any[]
  setAuthAppToShow: any
  handleToggleGlobalApp: any
  apiKey: string
  handleGenerateApiKey: () => void
  webhookUrl: string
  setWebhookUrl: (url: string) => void
  handleSaveWebhook: () => void
  handleOpenMarketplaceTab?: () => void
  handleOpenApp?: (app: any) => void
  hiddenAppIds?: string[]
  setHiddenAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  dockAppIds?: string[]
  setDockAppIds?: React.Dispatch<React.SetStateAction<string[]>>
  devSubTab?: string
  setDevSubTab?: (subTab: string) => void
  lang: Language
  user?: UserProfile | null
  desktopGridCols?: number
  setDesktopGridCols?: (cols: number) => void
  desktopIconSize?: number
  setDesktopIconSize?: (size: number) => void
  desktopShowLabels?: boolean
  setDesktopShowLabels?: (show: boolean) => void
}

export function SettingsView({
  isMobile,
  settingsTab,
  setSettingsTab,
  t,
  profileRole,
  currentAccount,
  handleSaveCompany,
  companyName,
  setCompanyName,
  companyDocument,
  setCompanyDocument,
  companyLogoUrl,
  setCompanyLogoUrl,
  companyBannerUrl: propCompanyBannerUrl,
  setCompanyBannerUrl: propSetCompanyBannerUrl,
  companyPrimaryColor,
  setCompanyPrimaryColor,
  companyCustomDomain,
  setCompanyCustomDomain,
  companySamlEnabled,
  setCompanySamlEnabled,
  companySamlIdpEntityId,
  setCompanySamlIdpEntityId,
  companySamlSsoUrl,
  setCompanySamlSsoUrl,
  companySamlCertificate,
  setCompanySamlCertificate,
  accounts,
  activeAccountId,
  handleSwitchWorkspace,
  handleCreateWorkspace,
  handleUpdateWorkspace,
  handleDeleteWorkspace,
  usersList,
  handleInviteUser,
  handleUpdateAccountMember,
  handleUpdateUserRole,
  handleRemoveUser,
  wallpaperTab,
  setWallpaperTab,
  mobileWallpaper,
  setMobileWallpaper,
  desktopWallpaper,
  setDesktopWallpaper,
  customWallpapers,
  setCustomWallpapers,
  useSeparateMobileWallpaper,
  setUseSeparateMobileWallpaper,
  handleSaveWallpaperToDb,
  dockPosition,
  setDockPosition,
  dockSize,
  setDockSize,
  dockAutoHide,
  setDockAutoHide,
  topBarConfig,
  setTopBarConfig,
  installedApps,
  handleToggleAppStatus,
  handleUninstallApp,
  handleInstallApp,
  handleRenameApp,
  availableWidgets,
  setWidgetsConfig,
  pinnedWidgets,
  handleToggleWidgetPin,
  marketplaceApps,
  globallyInstalledApps,
  developerApps,
  setAuthAppToShow,
  handleToggleGlobalApp,
  apiKey,
  handleGenerateApiKey,
  webhookUrl,
  setWebhookUrl,
  handleSaveWebhook,
  handleOpenMarketplaceTab,
  handleOpenApp,
  hiddenAppIds = [],
  setHiddenAppIds,
  dockAppIds,
  setDockAppIds,
  devSubTab = "api-keys",
  setDevSubTab,
  lang,
  user,
  desktopGridCols,
  setDesktopGridCols,
  desktopIconSize,
  setDesktopIconSize,
  desktopShowLabels,
  setDesktopShowLabels,
  workspaceRole = "member",
}: SettingsViewProps) {
  const isCompanyAdmin = profileRole === "admin" || profileRole === "owner"
  const isWorkspaceAdmin = isCompanyAdmin || workspaceRole === "admin"

  React.useEffect(() => {
    if (!isWorkspaceAdmin) {
      const workspaceAdminOnlyTabs = ["apps-list-tab", "widgets-list-tab"]
      if (workspaceAdminOnlyTabs.includes(settingsTab)) {
        setSettingsTab("wallpaper-tab")
      }
    }
    if (!isCompanyAdmin) {
      const companyAdminOnlyTabs = ["developer"]
      if (companyAdminOnlyTabs.includes(settingsTab)) {
        setSettingsTab("wallpaper-tab")
      }
    }
  }, [isCompanyAdmin, isWorkspaceAdmin, settingsTab, setSettingsTab])

  return (
    <Tabs
      orientation={isMobile ? "horizontal" : "vertical"}
      value={settingsTab}
      onValueChange={(value: any) => setSettingsTab(value)}
      className="flex flex-col md:flex-row flex-1 h-full overflow-hidden"
    >
      {/* Sidebar Esquerda */}
      {(!isMobile || settingsTab === "menu") && (
        <div className="w-full md:w-60 bg-zinc-50 dark:bg-zinc-950 border-r border-zinc-150 dark:border-zinc-800 p-4 flex flex-col justify-between shrink-0 select-none font-sans h-full">
          <div className="space-y-4">
            <div className="flex items-center gap-2 px-2 py-1">
              <div className="flex size-7 items-center justify-center rounded bg-[#004EB1]">
                <Settings className="size-4 text-white" />
              </div>
              <span className="font-bold text-xs text-zinc-900 dark:text-white uppercase tracking-wider">
                {t.settingsTitle}
              </span>
            </div>

            <div className="space-y-4 text-[10px]">
              <div className="space-y-1">
                <div className="px-3 text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                  {t.settingsCategoryCorporate}
                </div>
                <TabsList className="flex flex-col gap-0.5 w-full bg-transparent border-0 p-0 h-auto">
                  <TabsTrigger
                    value="company"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "company"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <Briefcase className="size-4 shrink-0" />
                    {t.settingsTabCompany}
                  </TabsTrigger>

                  <TabsTrigger
                    value="workspaces"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "workspaces"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <FolderOpen className="size-4 shrink-0" />
                    {t.settingsTabWorkspaces}
                  </TabsTrigger>

                  <TabsTrigger
                    value="users"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "users"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <UserCheck className="size-4 shrink-0 text-[#004EB1] dark:text-blue-400" />
                    {t.settingsTabUsers}
                  </TabsTrigger>
                </TabsList>
              </div>

              <div className="space-y-1">
                <div className="px-3 text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                  Aparência & Interface
                </div>
                <TabsList className="flex flex-col gap-0.5 w-full bg-transparent border-0 p-0 h-auto">
                  <TabsTrigger
                    value="wallpaper-tab"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "wallpaper-tab"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <Sliders className="size-4 shrink-0" />
                    Desk
                  </TabsTrigger>

                  <TabsTrigger
                    value="dock"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "dock"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <PanelRight className="size-4 shrink-0" />
                    Dock
                  </TabsTrigger>

                  <TabsTrigger
                    value="bar-tab"
                    className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "bar-tab"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                      }`}
                  >
                    <Clock className="size-4 shrink-0" />
                    Bar
                  </TabsTrigger>
                </TabsList>
              </div>

              {isWorkspaceAdmin && (
                <div className="space-y-1">
                  <div className="px-3 text-[9px] font-bold text-zinc-400 dark:text-zinc-500 uppercase tracking-wider">
                    Aplicativos & Widgets
                  </div>
                  <TabsList className="flex flex-col gap-0.5 w-full bg-transparent border-0 p-0 h-auto">
                    <TabsTrigger
                      value="apps-list-tab"
                      className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "apps-list-tab"
                        ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                        : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                        }`}
                    >
                      <Grid className="size-4 shrink-0" />
                      Aplicativos
                    </TabsTrigger>

                    <TabsTrigger
                      value="widgets-list-tab"
                      className={`flex items-center gap-2.5 w-full px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "widgets-list-tab"
                        ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold"
                        : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50"
                        }`}
                    >
                      <Puzzle className="size-4 shrink-0" />
                      Widgets
                    </TabsTrigger>
                  </TabsList>
                </div>
              )}
            </div>

            {isCompanyAdmin && (
              <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800 space-y-1 select-none">
                <TabsList className="flex flex-col w-full bg-transparent border-0 p-0 h-auto">
                  <TabsTrigger
                    value="developer"
                    className={`flex items-center gap-2.5 w-full px-3 py-2 text-xs font-bold rounded-lg transition-colors cursor-pointer text-left justify-start border-0 shadow-none outline-none ${settingsTab === "developer"
                      ? "bg-[#004EB1]/5 text-[#004EB1] dark:bg-blue-950/30 dark:text-blue-400 font-bold font-sans"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 hover:bg-zinc-100/50 dark:hover:bg-zinc-900/50 font-sans"
                      }`}
                  >
                    <Wrench className="size-4 shrink-0 text-amber-500 animate-pulse" />
                    {t.settingsTabDeveloper || "Integrações & Conexões"}
                  </TabsTrigger>
                </TabsList>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Painel da Direita (Conteúdo com scroll vertical) */}
      {(!isMobile || settingsTab !== "menu") && (
        <div className="flex-1 min-h-0 bg-white dark:bg-zinc-900 p-6 pb-24 overflow-y-auto h-full scroll-fade font-sans">
          {isMobile && (
            <button
              onClick={() => setSettingsTab("menu")}
              className="mb-4 flex items-center gap-1.5 text-xs font-bold text-[#004EB1] dark:text-blue-400 bg-blue-500/10 dark:bg-blue-500/20 px-3 py-1.5 rounded-lg w-fit transition-colors cursor-pointer shrink-0"
            >
              ← Voltar para Ajustes
            </button>
          )}
          <div className="flex-1 space-y-6 pb-12">
            {/* 1. Aba Empresa */}
            <CompanySettingsTab
              currentAccount={currentAccount}
              companyName={companyName}
              setCompanyName={setCompanyName}
              companyDocument={companyDocument}
              setCompanyDocument={setCompanyDocument}
              companyLogoUrl={companyLogoUrl}
              setCompanyLogoUrl={setCompanyLogoUrl}
              companyBannerUrl={propCompanyBannerUrl}
              setCompanyBannerUrl={propSetCompanyBannerUrl}
              companyPrimaryColor={companyPrimaryColor}
              setCompanyPrimaryColor={setCompanyPrimaryColor}
              companyCustomDomain={companyCustomDomain}
              setCompanyCustomDomain={setCompanyCustomDomain}
              handleSaveCompany={handleSaveCompany}
              profileRole={profileRole}
              t={t}
            />

            {/* 2. Aba Workspaces */}
            <WorkspacesSettingsTab
              currentAccount={currentAccount}
              handleCreateWorkspace={handleCreateWorkspace}
              handleUpdateWorkspace={handleUpdateWorkspace}
              handleDeleteWorkspace={handleDeleteWorkspace}
              usersList={usersList}
              t={t}
              profileRole={profileRole}
              user={user}
            />

            {/* 3. Aba Usuários & Perfis */}
            <UsersSettingsTab
              usersList={usersList}
              user={user}
              profileRole={profileRole}
              currentAccount={currentAccount}
              activeAccountId={activeAccountId}
              installedApps={installedApps}
              handleInviteUser={handleInviteUser}
              handleUpdateAccountMember={handleUpdateAccountMember}
              handleUpdateUserRole={handleUpdateUserRole || (() => { })}
              handleRemoveUser={handleRemoveUser}
              t={t}
            />

            {/* 4. Aba Desktop (Wallpaper, Widgets, Grid) */}
            <DeskSettingsTab
              wallpaperTab={wallpaperTab}
              setWallpaperTab={setWallpaperTab}
              mobileWallpaper={mobileWallpaper}
              setMobileWallpaper={setMobileWallpaper}
              desktopWallpaper={desktopWallpaper}
              setDesktopWallpaper={setDesktopWallpaper}
              customWallpapers={customWallpapers}
              setCustomWallpapers={setCustomWallpapers}
              useSeparateMobileWallpaper={useSeparateMobileWallpaper}
              handleSaveWallpaperToDb={handleSaveWallpaperToDb}
              availableWidgets={availableWidgets}
              setWidgetsConfig={setWidgetsConfig}
              desktopGridCols={desktopGridCols}
              setDesktopGridCols={setDesktopGridCols}
              desktopIconSize={desktopIconSize}
              setDesktopIconSize={setDesktopIconSize}
              desktopShowLabels={desktopShowLabels}
              setDesktopShowLabels={setDesktopShowLabels}
              user={user}
            />

            {/* 5. Aba Dock */}
            <DockSettingsTab
              dockPosition={dockPosition}
              setDockPosition={setDockPosition}
              dockSize={dockSize}
              setDockSize={setDockSize}
              dockAutoHide={dockAutoHide}
              setDockAutoHide={setDockAutoHide}
              dockAppIds={dockAppIds}
              setDockAppIds={setDockAppIds}
              installedApps={installedApps}
            />

            {/* 6. Aba Bar (Barra Superior) */}
            <BarSettingsTab
              topBarConfig={topBarConfig}
              setTopBarConfig={setTopBarConfig}
              user={user}
            />

            {/* 7. Aba Aplicativos */}
            {isWorkspaceAdmin && (
              <AppsSettingsTab
                installedApps={installedApps}
                handleToggleAppStatus={handleToggleAppStatus}
                handleUninstallApp={handleUninstallApp}
                handleInstallApp={handleInstallApp}
                handleRenameApp={handleRenameApp}
                handleOpenMarketplaceTab={handleOpenMarketplaceTab}
                setSettingsTab={setSettingsTab}
                isCompanyAdmin={isCompanyAdmin}
              />
            )}

            {/* 8. Aba Widgets */}
            {isWorkspaceAdmin && (
              <WidgetsSettingsTab
                availableWidgets={availableWidgets}
                setWidgetsConfig={setWidgetsConfig}
                handleOpenMarketplaceTab={handleOpenMarketplaceTab}
                setSettingsTab={setSettingsTab}
                isCompanyAdmin={isCompanyAdmin}
              />
            )}

            {/* 9. Aba Desenvolvedor & Integrações */}
            {isCompanyAdmin && (
              <DeveloperSettingsTab
                devSubTab={devSubTab}
                setDevSubTab={setDevSubTab}
                apiKey={apiKey}
                handleGenerateApiKey={handleGenerateApiKey}
                webhookUrl={webhookUrl}
                setWebhookUrl={setWebhookUrl}
                handleSaveWebhook={handleSaveWebhook}
                companySamlEnabled={companySamlEnabled}
                setCompanySamlEnabled={setCompanySamlEnabled}
                companySamlIdpEntityId={companySamlIdpEntityId}
                setCompanySamlIdpEntityId={setCompanySamlIdpEntityId}
                companySamlSsoUrl={companySamlSsoUrl}
                setCompanySamlSsoUrl={setCompanySamlSsoUrl}
                companySamlCertificate={companySamlCertificate}
                setCompanySamlCertificate={setCompanySamlCertificate}
                currentAccount={currentAccount}
                activeAccountId={activeAccountId}
                t={t}
              />
            )}
          </div>
        </div>
      )}
    </Tabs>
  )
}
